import { Navigate } from "react-router-dom";
import { InstrumentTypeResponse } from "../../codegen-api";
import { ReactComponent as FundingLogo } from "../../assets/svg/dollar.svg";
import { ReactComponent as SettlementLogo } from "../../assets/svg/settlement.svg";
import {
  AccountSurfacePosition,
  getAccountSurfaceFlags,
  shouldShowInstrumentAccountUi,
} from "../../config/features";
import { PageEndpointEnum } from "../../enums/endpoint";
import { FundingHistoryTab } from "../FundingHistoryTab";
import SettlementHistoryTab from "../SettlementHistoryTab";
import { IPage } from "../shared";

/**
 * The /historical tabs, shared by the desktop and mobile pages.
 *
 * Settlement history is option expiries, so it is shown only while options are
 * offered (or the account still holds an option); funding history is perp-only
 * (`showPerpUi`). The catch-all lands on the FIRST visible tab -- it used to
 * hard-code settlement history, the one tab a spot or perps-only build should
 * not open on. With no tab at all (a spot-only account) it goes to trading.
 */
export function buildHistoricalTabs({
  t,
  activePage,
  positions,
  isMobile,
}: {
  t: (key: string) => string;
  activePage?: string;
  positions?: AccountSurfacePosition[] | null;
  isMobile?: boolean;
}): IPage[] {
  const showSettlement = shouldShowInstrumentAccountUi(
    InstrumentTypeResponse.Option,
    positions
  );
  const { showPerpUi } = getAccountSurfaceFlags(positions);

  const tabs: IPage[] = [
    ...(showSettlement
      ? [
          {
            title: t("settlement_history"),
            path: "/settlement-history",
            redirect: `${PageEndpointEnum.HISTORICAL}/settlement-history`,
            Logo: SettlementLogo,
            element: <SettlementHistoryTab isMobile={isMobile} />,
            isActive: activePage === "settlement-history",
          },
        ]
      : []),
    ...(showPerpUi
      ? [
          {
            title: t("funding_history"),
            path: "/funding-history",
            redirect: `${PageEndpointEnum.HISTORICAL}/funding-history`,
            Logo: FundingLogo,
            element: <FundingHistoryTab isMobile={isMobile} />,
            isActive: activePage === "funding-history",
          },
        ]
      : []),
  ];

  const fallback = tabs[0]?.redirect ?? PageEndpointEnum.TRADING;
  return [
    ...tabs,
    {
      title: "",
      path: "*",
      redirect: fallback,
      element: <Navigate to={fallback} replace />,
      isActive: false,
    },
  ];
}
